import React, { useEffect, useMemo, useState } from "react";
import { api, formatError } from "../lib/api";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { Camera } from "lucide-react";

const MAX = 6;
const pill = { pending: "bg-amber-100 text-amber-800", approved: "bg-green-100 text-green-800", rejected: "bg-red-50 text-red-800" };

export function ReportStatusList({ reports }) {
  if (!reports.length) return null;
  return (
    <div className="mt-3 space-y-1">
      {reports.map((r) => (
        <div key={r.id} data-testid={`my-report-${r.id}`} className="flex items-center gap-2 text-xs text-[#63534E]">
          <span className={`px-2 py-0.5 rounded-full capitalize ${pill[r.status]}`}>{r.status}</span>
          <span className="truncate">{r.title} · {r.photo_urls.length} photo{r.photo_urls.length > 1 ? "s" : ""}</span>
          {r.status === "rejected" && r.review_note && <span className="text-red-700">— {r.review_note}</span>}
        </div>
      ))}
    </div>
  );
}

export default function ImpactReportForm({ project, onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", body: "" });
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const previews = useMemo(() => files.map((f) => ({ key: f.name + f.size, url: URL.createObjectURL(f) })), [files]);
  useEffect(() => () => previews.forEach((p) => URL.revokeObjectURL(p.url)), [previews]);
  const pick = (e) => {
    const list = Array.from(e.target.files || []);
    if (list.length > MAX) toast.error(`Up to ${MAX} photos — keeping the first ${MAX}`);
    setFiles(list.slice(0, MAX));
  };
  const submit = async (e) => {
    e.preventDefault();
    if (!files.length) return toast.error("Add at least one photo");
    setBusy(true);
    const fd = new FormData();
    fd.append("title", form.title); fd.append("body", form.body);
    files.forEach((f) => fd.append("photos", f));
    try {
      await api.post(`/ngos/projects/${project.id}/reports`, fd);
      toast.success("Update sent — it will appear publicly after our team reviews it");
      setForm({ title: "", body: "" }); setFiles([]); setOpen(false); onSaved();
    } catch (err) { toast.error(formatError(err)); }
    setBusy(false);
  };
  if (project.status === "rejected") return null;
  if (!open) return <button data-testid={`report-open-${project.id}`} onClick={() => setOpen(true)} className="text-sm text-[#E65100] font-semibold flex items-center gap-1"><Camera className="w-4 h-4" />Post an impact update</button>;
  return (
    <form data-testid={`report-form-${project.id}`} onSubmit={submit} className="mt-3 w-full space-y-3 bg-white border border-[#D4AF37]/30 rounded-xl p-4">
      <Input data-testid="report-title" required minLength={5} maxLength={120} placeholder="Headline, e.g. 40 children started classes" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
      <Textarea data-testid="report-body" required minLength={20} maxLength={3000} rows={4} placeholder="What happened, how the money was used, who benefited…" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} />
      <label className="block text-sm text-[#63534E]">Photos (1–{MAX}, max 5MB each)
        <input data-testid="report-photos" type="file" accept="image/*" multiple onChange={pick} className="block mt-1 text-sm" />
      </label>
      {previews.length > 0 && <div className="flex gap-2 flex-wrap">{previews.map((p) => <img key={p.key} src={p.url} alt="" className="w-16 h-16 object-cover rounded-lg" />)}</div>}
      <div className="flex gap-2">
        <Button data-testid="report-submit" disabled={busy} className="btn-saffron rounded-full">{busy ? "Uploading…" : "Send for review"}</Button>
        <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
      </div>
    </form>
  );
}
